import { useRef, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { CheckCircle2, FileText, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { API, BOOKING_URL, isRealBookingUrl, PRIVACY_POLICY_URL } from "../config";
import { trackEvent, trackLead, getAttribution } from "../lib/analytics";

const inputCls =
  "w-full rounded-xl border border-ink/10 bg-elevated px-4 py-3.5 text-sm text-ink outline-none transition-colors placeholder:text-ink/35 focus:border-mint";

const VOLUME_OPTIONS = [
  "Under $25,000",
  "$25,000–$50,000",
  "$50,000–$100,000",
  "$100,000–$250,000",
  "$250,000+",
  "I'm not sure",
];

const formatPhone = (v) => {
  const d = v.replace(/\D/g, "").slice(0, 10);
  if (d.length > 6) return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
  if (d.length > 3) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
  if (d.length > 0) return `(${d}`;
  return "";
};

export const HeroLeadForm = () => {
  const [form, setForm] = useState({ name: "", business_name: "", email: "", phone: "", volume_range: "", sms_consent: false });
  const [step, setStep] = useState("form"); // form | code | done
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [code, setCode] = useState("");
  const [upload, setUpload] = useState("idle"); // idle | working | done | error
  const fileRef = useRef(null);
  const started = useRef(false);

  const markStart = () => {
    if (!started.current) {
      started.current = true;
      trackEvent("form_start", { form: "hero" });
    }
  };

  const sendCode = async (e) => {
    e?.preventDefault();
    setError("");
    if (form.phone.replace(/\D/g, "").length !== 10) {
      setError("Enter a valid 10-digit US phone number.");
      return;
    }
    setBusy(true);
    try {
      await axios.post(`${API}/leads/verify/start`, { email: form.email });
      setStep("code");
      trackEvent("lead_verification_sent", { form: "hero" });
    } catch (err) {
      setError(err.response?.data?.detail || "Couldn't send the code — check the email and try again.");
    } finally {
      setBusy(false);
    }
  };

  const verifyAndSubmit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setError("");
    setBusy(true);
    try {
      await axios.post(`${API}/leads/verify/confirm`, { email: form.email, code });
      await axios.post(`${API}/leads`, {
        name: form.name,
        business_name: form.business_name,
        email: form.email,
        phone: form.phone,
        volume_range: form.volume_range,
        sms_consent: form.sms_consent,
        source: "hero_form",
        ...getAttribution(),
      });
      setStep("done");
      trackLead({ content_name: "free_savings_review", ...getAttribution() });
      trackEvent("lead_form_submitted", { source: "hero_form" });
      toast.success("Request received — we'll be in touch shortly.");
    } catch (err) {
      setError(err.response?.data?.detail || "Something went wrong — your information is still here, please try again.");
    } finally {
      setBusy(false);
    }
  };

  const uploadStatement = async (file) => {
    if (!file || upload === "working") return;
    trackEvent("statement_upload_started", { source: "post_lead" });
    setUpload("working");
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("lead_email", form.email);
      await axios.post(`${API}/statements`, fd);
      setUpload("done");
      trackEvent("statement_upload_completed", { source: "post_lead" });
    } catch {
      setUpload("error");
    }
  };

  return (
    <div
      id="hero-form"
      data-testid="hero-form"
      className="w-full scroll-mt-28 rounded-3xl border border-ink/10 bg-surface p-7 shadow-2xl shadow-black/30 sm:p-8"
    >
      {step === "form" && (
        <form onSubmit={sendCode} className="space-y-3.5">
          <h2 className="font-display text-2xl font-extrabold tracking-tight">
            Request Your <span className="text-mint">Free Savings Review</span>
          </h2>
          <p className="text-sm leading-relaxed text-white/60">
            Tell us a little about your business. A payment specialist will follow up to discuss
            your processing costs and next steps.
          </p>
          <input required data-testid="hero-name-input" placeholder="Full name" autoComplete="name"
            value={form.name} onFocus={markStart}
            onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} />
          <input required data-testid="hero-business-input" placeholder="Business name" autoComplete="organization"
            value={form.business_name}
            onChange={(e) => setForm({ ...form, business_name: e.target.value })} className={inputCls} />
          <input required type="email" data-testid="hero-email-input" placeholder="Email address" autoComplete="email"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputCls} />
          <input required type="tel" data-testid="hero-phone-input" placeholder="Phone number" autoComplete="tel"
            value={form.phone}
            onChange={(e) => setForm({ ...form, phone: formatPhone(e.target.value) })} className={inputCls} />
          <select required data-testid="hero-volume-select" value={form.volume_range}
            onChange={(e) => setForm({ ...form, volume_range: e.target.value })}
            className={`${inputCls} appearance-none ${form.volume_range ? "" : "text-ink/35"}`}>
            <option value="" disabled>Approximate monthly card processing volume</option>
            {VOLUME_OPTIONS.map((o) => (
              <option key={o} value={o} className="text-ink">{o}</option>
            ))}
          </select>
          <label className="flex items-start gap-3 text-left text-[11px] leading-relaxed text-white/50">
            <input type="checkbox" data-testid="hero-sms-consent" checked={form.sms_consent}
              onChange={(e) => setForm({ ...form, sms_consent: e.target.checked })}
              className="mt-0.5 h-4 w-4 shrink-0 accent-[#009CDE]" />
            <span>
              Optional: I agree to receive marketing text messages from CardConnect Commerce about my
              request at the number provided. Message frequency varies. Message and data rates may
              apply. Reply STOP to opt out. Consent is not required to request a review.
            </span>
          </label>
          {error && <p data-testid="hero-form-error" className="text-xs text-danger">{error}</p>}
          <button type="submit" disabled={busy} data-testid="hero-submit-button"
            className="w-full rounded-full bg-mint py-4 font-display text-base font-bold text-cream transition-transform duration-300 hover:scale-[1.02] hover:brightness-110 disabled:opacity-60">
            {busy ? "Sending code…" : "Request My Free Savings Review"}
          </button>
          <p className="text-center text-[11px] text-white/50">
            No obligation to switch. No statement required to request your review.
          </p>
          <p className="text-center text-[11px] leading-relaxed text-white/40">
            We'll email you a 6-digit code to confirm your email. By submitting, you agree to be
            contacted about your request. See our{" "}
            <a href={PRIVACY_POLICY_URL} target="_blank" rel="noopener noreferrer" data-testid="privacy-link"
              className="text-mint underline">
              Privacy Policy
            </a>
            .
          </p>
        </form>
      )}

      {step === "code" && (
        <form onSubmit={verifyAndSubmit} className="space-y-4" data-testid="hero-verify-form">
          <h2 className="font-display text-2xl font-extrabold tracking-tight">Check Your Inbox</h2>
          <p className="text-sm leading-relaxed text-white/60">
            We sent a 6-digit code to <span className="font-mono text-mint">{form.email}</span>.
            Enter it below to confirm your request.
          </p>
          <input required inputMode="numeric" maxLength={6} data-testid="hero-verify-code-input" placeholder="••••••"
            value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
            className="w-full rounded-xl border border-ink/10 bg-elevated px-4 py-4 text-center font-mono text-3xl tracking-[0.5em] text-ink outline-none transition-colors placeholder:text-ink/20 focus:border-mint" />
          {error && <p data-testid="hero-verify-error" className="text-xs text-danger">{error}</p>}
          <button type="submit" disabled={busy || code.length !== 6} data-testid="hero-verify-submit"
            className="w-full rounded-full bg-mint py-4 font-display text-base font-bold text-cream transition-transform duration-300 hover:scale-[1.02] hover:brightness-110 disabled:opacity-60">
            {busy ? "Verifying…" : "Verify & Request My Review →"}
          </button>
          <div className="flex justify-between font-mono text-xs text-white/40">
            <button type="button" data-testid="hero-verify-back"
              onClick={() => { setStep("form"); setError(""); setCode(""); }}
              className="transition-colors hover:text-mint">
              ← Edit my info
            </button>
            <button type="button" data-testid="hero-verify-resend" onClick={sendCode}
              className="transition-colors hover:text-mint">
              Resend code
            </button>
          </div>
        </form>
      )}

      {step === "done" && (
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} data-testid="hero-success" className="text-center">
          <CheckCircle2 size={44} className="mx-auto text-mint" />
          <h2 className="mt-4 font-display text-2xl font-extrabold tracking-tight">
            Your Review Request Is In
          </h2>
          <p className="mx-auto mt-3 max-w-sm text-sm leading-relaxed text-white/60">
            Thank you. A CardConnect Commerce payment specialist will follow up using the contact
            information you provided.
          </p>

          <div className="mt-7 border-t border-ink/10 pt-6 text-left">
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/40">
              Optional next steps
            </p>
            <p className="mt-4 font-display text-base font-bold">Have a Processing Statement Handy?</p>
            <p className="mt-1.5 text-xs leading-relaxed text-white/50">
              You can securely upload a recent statement to help us prepare for your review. Don't
              have it available? Your payment specialist can help you with the next step.
            </p>
            {upload === "done" ? (
              <p className="mt-4 flex items-center gap-2 text-sm font-bold text-mint" data-testid="hero-upload-done">
                <CheckCircle2 size={16} /> Statement received — thank you.
              </p>
            ) : (
              <button
                type="button"
                data-testid="hero-upload-button"
                disabled={upload === "working"}
                onClick={() => fileRef.current?.click()}
                className="mt-4 flex w-full items-center justify-center gap-2 rounded-xl border border-dashed border-mint/40 bg-mint/5 px-4 py-3.5 text-sm font-bold text-mint transition-colors hover:bg-mint/10 disabled:opacity-60"
              >
                <FileText size={16} />
                {upload === "working" ? "Analyzing your statement…" : upload === "error" ? "Upload failed — try again" : "Upload a recent statement (PDF, JPG, PNG)"}
              </button>
            )}
            <input ref={fileRef} type="file" accept=".pdf,.png,.jpg,.jpeg" className="hidden"
              data-testid="hero-upload-input"
              onChange={(e) => uploadStatement(e.target.files?.[0])} />
            {isRealBookingUrl && (
              <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer" data-testid="hero-schedule-link"
                className="mt-3 flex w-full items-center justify-center rounded-xl border border-ink/15 px-4 py-3.5 text-sm font-bold text-ink transition-colors hover:border-mint hover:text-mint">
                Choose a convenient time to talk →
              </a>
            )}
            <p className="mt-4 flex items-center gap-2 text-[10px] text-white/35">
              <ShieldCheck size={12} className="text-mint" />
              Statements are analyzed securely and never stored or shared with advertising platforms.
            </p>
          </div>
        </motion.div>
      )}
    </div>
  );
};
